import upperFirst from 'lodash-es/upperFirst'

import {
  PlacementId,
  fromPlacementId,
} from '../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import {
  EmbroideryTypeKey,
  UnavailabilityReason,
} from '../../../../../containers/EmbroideryContainer/combination-rules/types'
import { EmbroideryItem } from '../../../../../containers/EmbroideryContainer/providers/types'

export type PlacementCopy = {
  statusLogoAdded: string
  statusIconAdded: string
  statusTextAdded: string
  statusLogoIncluded: string
  statusIconIncluded: string
  statusTextIncluded: string
  statusTextLimitReached: string
  statusSleeveLimitReached: string
  statusUnavailable: string
  expandPlacement: string
  collapsePlacement: string
}

export const placementName = (placementId: PlacementId): string => {
  const { location, position } = fromPlacementId(placementId)
  return `${upperFirst(position.toLowerCase())} ${upperFirst(location.toLowerCase())}`
}

export const unavailabilityLabel = (reason: UnavailabilityReason, copy: PlacementCopy): string => {
  switch (reason) {
    case 'max-text-items':
      return copy.statusTextLimitReached
    case 'sleeve-text':
      return copy.statusSleeveLimitReached
    case 'one-text-per-placement':
      return copy.statusTextAdded
    default:
      return copy.statusUnavailable
  }
}

const occupantAddedLabel = (occupantTypeKey: EmbroideryTypeKey, copy: PlacementCopy): string => {
  switch (occupantTypeKey) {
    case 'logo':
      return copy.statusLogoAdded
    case 'icon':
      return copy.statusIconAdded
    case 'text':
      return copy.statusTextAdded
  }
}

const occupantIncludedLabel = (occupantTypeKey: EmbroideryTypeKey, copy: PlacementCopy): string => {
  switch (occupantTypeKey) {
    case 'logo':
      return copy.statusLogoIncluded
    case 'icon':
      return copy.statusIconIncluded
    case 'text':
      return copy.statusTextIncluded
  }
}

/** Icon and logo share the "one mark per placement" rule, so each is the other's mark conflict. */
const marks: readonly EmbroideryTypeKey[] = ['icon', 'logo']

/**
 * Names the occupant blocking this card, for reasons that mean "something else is already here".
 * `no-logo-over-sleeve-text` gets its own sleeve-specific wording since the sleeve is the actual
 * conflict.
 */
const occupancyOccupantLabel = (
  reason: UnavailabilityReason,
  otherTypeItem: EmbroideryItem,
  copy: PlacementCopy
): string | undefined => {
  if (reason === 'sleeve-text') return copy.statusSleeveLimitReached

  if (reason === 'no-logo-over-sleeve-text') {
    if (otherTypeItem.typeKey === 'text') return copy.statusTextAdded
    if (otherTypeItem.typeKey === 'logo') return copy.statusLogoAdded
  }

  if (
    reason === 'one-mark-per-placement' ||
    reason === 'one-text-per-placement' ||
    reason === 'position-capacity'
  ) {
    return occupantAddedLabel(otherTypeItem.typeKey, copy)
  }

  return undefined
}

/**
 * The " | Logo Added" (or "Unavailable - Logo Added" once the card is disabled) half of a placement
 * card's label. Without `blockingInstanceIds` an occupant of the current form's own type is skipped,
 * since that's just this form's own selection.
 */
export const placementStatusLabel = ({
  typeKey,
  placementId,
  reason,
  blockingInstanceIds,
  items,
  copy,
}: {
  typeKey: EmbroideryTypeKey
  placementId: PlacementId
  reason: UnavailabilityReason | undefined
  /**
   * The items the engine actually blamed for `reason` (`Violation.instanceIds`) — e.g. only the
   * icon at a chest that holds both an icon and text, for `one-mark-per-placement`
   */
  blockingInstanceIds: readonly string[] | undefined
  items: readonly EmbroideryItem[]
  copy: PlacementCopy
}): string | undefined => {
  const itemsAtPlacement = items.filter(item => item.placementId === placementId)
  const blockersHere =
    blockingInstanceIds === undefined
      ? itemsAtPlacement.filter(item => item.typeKey !== typeKey)
      : items.filter(item => blockingInstanceIds.includes(item.instanceId))
  // Several occupants can share the blame (`position-capacity` names everyone at a full spot),
  // but icon and logo are mutually exclusive by their own rule, so that conflict is the one
  // worth naming over a merely-crowded placement's other occupant (e.g. text).
  const otherTypeItem =
    (marks.includes(typeKey)
      ? blockersHere.find(item => marks.includes(item.typeKey))
      : undefined) ?? blockersHere[0]
  const detail =
    otherTypeItem === undefined || reason === undefined
      ? undefined
      : occupancyOccupantLabel(reason, otherTypeItem, copy)

  if (detail !== undefined) {
    return detail
  }

  if (reason === undefined) return includedOccupantLabel(typeKey, placementId, items, copy)

  return unavailabilityLabel(reason, copy)
}

/**
 * The " | Icon Included" half of an open card's label, naming another type's item already at this
 * placement. A logo names text only on the chest.
 */
const includedOccupantLabel = (
  typeKey: EmbroideryTypeKey,
  placementId: PlacementId,
  items: readonly EmbroideryItem[],
  copy: PlacementCopy
): string | undefined => {
  const itemsAtPlacement = items.filter(item => item.placementId === placementId)
  const occupant =
    typeKey === 'text'
      ? itemsAtPlacement.find(item => marks.includes(item.typeKey))
      : typeKey === 'logo' && fromPlacementId(placementId).location !== 'CHEST'
      ? undefined
      : itemsAtPlacement.find(item => item.typeKey === 'text')

  return occupant === undefined ? undefined : occupantIncludedLabel(occupant.typeKey, copy)
}

/** Fills the `{{placement}}` slot in the disclosure button's accessible name. */
export const withPlacementName = (template: string, placementId: PlacementId): string =>
  template.replace('{{placement}}', placementName(placementId))

/** The " · $5" suffix appended to a placement's name when it carries a price. */
export const priceSuffix = (priceLabel: string | undefined): string =>
  priceLabel === undefined ? '' : ` · ${priceLabel}`

/** The " | Logo Added" suffix appended to a placement's name when it has a status. */
export const statusSuffix = (statusLabel: string | undefined): string =>
  statusLabel === undefined ? '' : ` | ${statusLabel}`

/** The full spoken name for a placement card, for use as an `aria-label`. */
export const placementSpokenLabel = (
  placementId: PlacementId,
  priceLabel: string | undefined,
  statusLabel: string | undefined
): string => `${placementName(placementId)}${priceSuffix(priceLabel)}${statusSuffix(statusLabel)}`
